import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { CompanyLifeState, CompanyStateTimelineCompositeEntity } from '@nk/data-entities-lib';

/**
 * If none of these are set, assume company to be normal/healthy
 */
export const COMPANY_LIFE_STATES = {
  registered_bankrupt: 'registered_bankrupt',
  registered_liquidation: 'registered_liquidation',
  registered_forced_liquidation: 'registered_forced_liquidation',
  confirmed_dead: 'confirmed_dead',
  under_reconstruction: 'under_reconstruction',
} as const;

type CompanyLifeStateKey = keyof typeof COMPANY_LIFE_STATES;

export const COMPANY_LIFE_STATE_TRANSLATIONS = {
  registered_bankrupt: extractTranslation('company.company_life_state.registered_bankrupt'),
  registered_liquidation: extractTranslation('company.company_life_state.registered_liquidation'),
  registered_forced_liquidation: extractTranslation('company.company_life_state.registered_forced_liquidation'),
  confirmed_dead: extractTranslation('company.company_life_state.confirmed_dead'),
  under_reconstruction: extractTranslation('company.company_life_state.under_reconstruction'),
};

export const NON_OPERATIONAL_COMPANY_STATES: CompanyLifeStateKey[] = [
  COMPANY_LIFE_STATES.confirmed_dead,
  COMPANY_LIFE_STATES.registered_bankrupt,
  COMPANY_LIFE_STATES.registered_forced_liquidation,
  COMPANY_LIFE_STATES.registered_liquidation,
];

const COMPANY_LIFE_STATE_PRIORITY = [
  COMPANY_LIFE_STATES.confirmed_dead,
  COMPANY_LIFE_STATES.registered_bankrupt,
  COMPANY_LIFE_STATES.registered_forced_liquidation,
  COMPANY_LIFE_STATES.registered_liquidation,
  COMPANY_LIFE_STATES.under_reconstruction,
] as CompanyLifeStateKey[];

export const COMPANY_LIFE_STATE_COLOR_CLASSES: Record<keyof typeof COMPANY_LIFE_STATES, string> = {
  registered_bankrupt: 'danger',
  registered_liquidation: 'warning',
  registered_forced_liquidation: 'danger',
  confirmed_dead: 'gray',
  under_reconstruction: 'warning',
};

function getCompanyStateByKey(
  states: CompanyStateTimelineCompositeEntity[],
  key: keyof typeof COMPANY_LIFE_STATES,
): CompanyStateTimelineCompositeEntity | null {
  return states.find((i) => i.company_state.company_state_category_key === key) ?? null;
}

function hasStateCategoryKey(
  states: CompanyStateTimelineCompositeEntity[],
  companyStateCategoryKey: string,
  companyStateKey: string,
): boolean {
  return states.some((state) => {
    const cat = state.company_state.company_state_category_key;
    const key = state.company_state.company_state_key;
    return cat === companyStateCategoryKey && key === companyStateKey;
  });
}
export function companyLifeStateFromTimeline(
  states: CompanyStateTimelineCompositeEntity[], // Must include current state for a company.
): CompanyLifeState {
  const registeredBankrupt = hasStateCategoryKey(states, COMPANY_LIFE_STATES.registered_bankrupt, 'true');
  const registeredLiquidation = hasStateCategoryKey(states, COMPANY_LIFE_STATES.registered_liquidation, 'true');
  const registeredForcedLiquidation = hasStateCategoryKey(
    states,
    COMPANY_LIFE_STATES.registered_forced_liquidation,
    'true',
  );
  const confirmedDead = hasStateCategoryKey(states, COMPANY_LIFE_STATES.confirmed_dead, 'true');
  const underReconstruction = hasStateCategoryKey(states, COMPANY_LIFE_STATES.under_reconstruction, 'true');

  const notHealthy = registeredBankrupt || registeredLiquidation || registeredForcedLiquidation || confirmedDead;

  const companyLifeState: CompanyLifeState = {
    states: {
      registered_bankrupt: registeredBankrupt,
      registered_liquidation: registeredLiquidation,
      registered_forced_liquidation: registeredForcedLiquidation,
      confirmed_dead: confirmedDead,
      under_reconstruction: underReconstruction,
      // If you want to add more here, remember to update the service: getCompanyLifeState
    },
    healthy: !notHealthy,
    mainState: null,
    mainStateKey: null,
  };
  for (const key of COMPANY_LIFE_STATE_PRIORITY) {
    if (companyLifeState.states[key]) {
      companyLifeState.mainState = getCompanyStateByKey(states, key);
      companyLifeState.mainStateKey = key;
      break;
    }
  }

  return companyLifeState;
}
